import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { ErrorMessage, Form, Formik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../../components/Button/Button';
import Input from '../../../../../../components/Input/Input';
import Spinner from '../../../../../../components/Spinner/Spinner';
import { useAppDispatch, useAppSelector } from '../../../../../../reduxService/hooks';
import { ISubSectorResponse } from '../../../../api/IApiResponse';
import * as BeMaccaferriStoreHelper from '../../../../../api/store/BeMaccaferriStoreHelper';
import { SubSectorPosition } from '../../../../enum/Sector';
import { selectProject } from '../../../../selector/conversion';
import { IMaterialSubSectorSlice, setMaterialToSubSector } from '../../../../store/project/projectSlice';
import { IFormikSector, PaperComponent } from '../ModalMaterial/FormikMaterial';
import { IFormikMaterialProps } from '../ModalMaterial/IModalMaterial';
import './ModalSubsector.scss';
import { RadioSectorBtns } from './RadioSectorBtns/RadioSectorBtns';
import { RadioSubSectorBtns } from './RadioSectorBtns/RadioSubSectorBtns';
import { SubsectorBox } from './SubsectorBox/SubsectorBox';
import { cleanResults, setDischargeAlert } from '../../../../store/project/resultsSlice';
import * as Alert from '../../../../../../components/Alert/Alert';
import { conversionLabel } from '../../../../../conversion';
import { selectProjectInfo } from '../../../../../store/projectInfo/projectInfoSlice';

export interface IFormikFormSubsectors extends IFormikSector {
    [key: number]: ISubSectorResponse;
    length?: number;
    subSectorNumber: SubSectorPosition;
}
export interface IFormikFormSectors {
    [key: number]: IFormikFormSubsectors;
    choosenSector: number;
}

export const FormikSubsector = (props: IFormikMaterialProps) => {
    const { open, closeModal } = props;
    const [formikInitialValue, setFormikInitialValue] = useState<IFormikFormSectors>();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const project = useAppSelector(selectProject);
    const projectInfo = useAppSelector(selectProjectInfo);

    const getMaterialFromSector = useCallback(
        async (sectors: IFormikFormSectors) => {
            const sector = sectors[3];
            if (!sector.material && !!sector.materialId) {
                let responseMaterial = await BeMaccaferriStoreHelper.getMaterialByIdAsync(sector.materialId);
                sectors[3].material = responseMaterial.data;
                sectors[3][SubSectorPosition.Right].material = responseMaterial.data;
            }
            setFormikInitialValue(sectors);
        },
        [project],
    );

    useEffect(() => {
        let sectors: IFormikFormSectors = { choosenSector: 3 };
        if (!!project.data && !!project.data.geometry && !formikInitialValue) {
            project.data.geometry.forEach(sector => {
                let field = sector.channel;
                sectors[field] = { ...sector, length: sector.length, subSectorNumber: !!sector.subSectors?.length ? sector.subSectors?.length : 0 };
                if (!!sector.subSectors && sector.subSectors.length > 0) {
                    sector.subSectors.forEach(subSector => {
                        let pos = subSector.position;
                        sectors[field][pos] = { ...subSector, isTouched: true };
                    });
                    sectors[field].subSectorNumber = sector.subSectors.length - 1;
                } else {
                    sectors[field][SubSectorPosition.Right] = {
                        position: SubSectorPosition.Right,
                        length: sector.length,
                        material: sector.material,
                    };
                }
            });
            setFormikInitialValue(sectors);
        }
    }, [project, formikInitialValue]);

    return (
        <>
            {props.open && (
                <>
                    <Dialog style={{ zIndex: 900 }} open={open} onClose={() => closeModal()} PaperComponent={PaperComponent} aria-labelledby='draggable-dialog-title' className='dialog-material'>
                        {formikInitialValue ? (
                            <Formik
                                enableReinitialize
                                initialValues={formikInitialValue}
                                validate={values => {
                                    const errors: any = {};
                                    return errors;
                                }}
                                onSubmit={(values, actions) => {
                                    let subs: ISubSectorResponse[] = [];
                                    const subSectRight = values[values.choosenSector][0].isTouched;
                                    const subSectLeft = values[values.choosenSector][1]?.isTouched;
                                    const subSectCenter = values[values.choosenSector][2]?.isTouched;
                                    if (subSectRight) subs.push(values[values.choosenSector][0]);
                                    if (subSectLeft) subs.push(values[values.choosenSector][1]);
                                    if (subSectCenter) subs.push(values[values.choosenSector][2]);

                                    let materialSubsectorSlice: IMaterialSubSectorSlice = {
                                        sectorId: values.choosenSector,
                                        subsectors: subs,
                                    };
                                    const rightLength = values[values.choosenSector][0].length;
                                    const leftLength = values[values.choosenSector][1]?.length;
                                    const centerLength = values[values.choosenSector][2]?.length;

                                    if (!!subSectRight && !subSectLeft && !subSectCenter) {
                                        dispatch(cleanResults());
                                        dispatch(
                                            setMaterialToSubSector({
                                                sectorId: values.choosenSector,
                                                subsectors: [],
                                            }),
                                        );
                                        dispatch(setDischargeAlert(false));
                                        Alert.toastSuccess(t('modal_subsector_saved'));
                                    } else if (!!rightLength && (!!leftLength || !!centerLength)) {
                                        dispatch(cleanResults());
                                        dispatch(setMaterialToSubSector(materialSubsectorSlice));
                                        dispatch(setDischargeAlert(false));
                                        Alert.toastSuccess(t('modal_subsector_saved'));
                                    }
                                }}>
                                {({ values, handleSubmit, handleChange, setFieldValue }) => {
                                    const rightLength = values[values.choosenSector][0].length;
                                    const leftLength = values[values.choosenSector][1]?.length;
                                    const centerLength = values[values.choosenSector][2]?.length;
                                    let disabledConfirm = false;
                                    if (!!rightLength && (!!leftLength || !!centerLength)) {
                                        let ll = !!leftLength ? leftLength : 0;
                                        let cl = !!centerLength ? centerLength : 0;
                                        let csl = values[values.choosenSector].length ?? 0;
                                        disabledConfirm = ll + cl + rightLength > csl;
                                    }
                                    if (!!rightLength && !leftLength && !centerLength) {
                                        disabledConfirm = true;
                                    }
                                    return (
                                        <Form>
                                            <div className='modal-subsectors'>
                                                <DialogTitle style={{ cursor: 'move' }} id='draggable-dialog-title'>
                                                    {t('subsector')}
                                                </DialogTitle>
                                                <DialogContent>
                                                    <div className='d-flex justify-content-between'>
                                                        <RadioSectorBtns />
                                                        <RadioSubSectorBtns />
                                                    </div>
                                                    <div className='d-flex gap-3 align-items-center justify-content-between'>
                                                        <label className='sector'>
                                                            {t('sector')}:<label className='choosenSector'>{values.choosenSector}</label>
                                                        </label>
                                                        <Input
                                                            type='number'
                                                            step={0.01}
                                                            title={t('modal_subsector_sector_lenght', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                                                            name='totalLength'
                                                            className='d-flex gap-2 align-items-center'
                                                            readonly
                                                            disabled
                                                            value={values[values.choosenSector].length?.toFixed(2)}
                                                        />
                                                    </div>
                                                    <SubsectorBox />
                                                </DialogContent>
                                                <DialogActions>
                                                    <div className='d-flex flex-column justify-content-end'>
                                                        <div className='d-flex justify-content-end gap-3'>
                                                            <Button type='button' onClick={() => closeModal()}>
                                                                {t('close_button')}
                                                            </Button>
                                                            <Button type='submit' onClick={() => handleSubmit()} disabled={disabledConfirm}>
                                                                <i className='fa-solid fa-check'></i>
                                                                {t('confirm_button')}
                                                            </Button>
                                                        </div>
                                                        <div>{disabledConfirm && <p> {t('disabledConfirm')}</p>}</div>
                                                    </div>
                                                </DialogActions>
                                            </div>
                                        </Form>
                                    );
                                }}
                            </Formik>
                        ) : (
                            <Spinner />
                        )}
                    </Dialog>
                </>
            )}
        </>
    );
};
